import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
    CurrencyDollar, Calculator, Plus, Trash, FileText, Calendar,
    Lightning, Printer, CheckCircle, ArrowsClockwise, DownloadSimple,
    Wallet, Receipt, Warning, SpinnerGap, ListMagnifyingGlass
} from '@phosphor-icons/react';
import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription
} from '../components/ui/sheet';
import {
    getPendingCashCut, createCashCut, listCashCuts, downloadCashCutPdf
} from '../services/cashCutsApi';
import { numberToSpanishMXN } from '../lib/numberToWords';
import { useAuth } from '../context/AuthContext';
import CashCutAuditModal from '../components/CashCutAuditModal';
import { ReceiptDayBoxes, ReceiptTotals, SalesDetailTable } from '../components/commissions/ReceiptView';

const PAYMENT_LABEL_ES = {
    cash: 'Efectivo',
    transfer: 'Transferencia',
    deposit: 'Depósito',
    credit_card: 'Tarjeta de crédito',
    debit_card: 'Tarjeta de débito',
    other: 'Otro',
};

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmt = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 }).format(n || 0);
const MX = { timeZone: 'America/Mexico_City', day: '2-digit', month: '2-digit', year: 'numeric' };
const fmtDateMx = (iso) => (iso ? new Date(iso).toLocaleDateString('es-MX', MX) : '');
const fmtDateTimeMx = (iso) => (iso ? new Date(iso).toLocaleString('es-MX', { ...MX, hour: '2-digit', minute: '2-digit' }) : '');

const fmtDate = (iso) => {
    if (!iso) return '';
    // Si viene en formato YYYY-MM-DD (sin hora), constrúyelo como fecha LOCAL
    // para evitar el bug de UTC→local que tira un día atrás (martes 16 → lunes 15).
    if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
        const [y, m, day] = iso.split('-').map(Number);
        return new Date(y, m - 1, day).toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' });
    }
    return new Date(iso).toLocaleDateString('es-MX', { timeZone: 'America/Mexico_City', weekday: 'long', day: 'numeric', month: 'long' });
};

const BONUS_TYPES = [
    { value: 'objective', label: 'Bono por objetivo' },
    { value: 'production', label: 'Bono por producción' },
    { value: 'extra_hours', label: 'Bono por horas extra' },
    { value: 'special', label: 'Bono especial' },
    { value: 'manual', label: 'Ajuste manual' }
];

const CommissionsPage = () => {
    const { can, isAdmin } = useAuth();
    const canAuditCuts = isAdmin() || can('cash_cuts.audit_detail');
    const [auditCutId, setAuditCutId] = useState(null);
    const [sellers, setSellers] = useState([]);
    const [history, setHistory] = useState([]);
    const [selectedSeller, setSelectedSeller] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [bonuses, setBonuses] = useState([]);
    const [deductions, setDeductions] = useState([]);
    const [paymentMethod, setPaymentMethod] = useState('cash');
    const [notes, setNotes] = useState('');
    const [preview, setPreview] = useState(null);
    const [showReceipt, setShowReceipt] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [company, setCompany] = useState({});
    // Cash cut state
    const [showCashCut, setShowCashCut] = useState(false);
    const [cashCutSeller, setCashCutSeller] = useState('');
    const [pendingCut, setPendingCut] = useState(null);
    const [cashReceived, setCashReceived] = useState('');
    const [cashCutNotes, setCashCutNotes] = useState('');
    const [cashCutUntilDate, setCashCutUntilDate] = useState(''); // YYYY-MM-DD (partial cut)
    const [loadingCut, setLoadingCut] = useState(false);
    const [savingCut, setSavingCut] = useState(false);
    const [cashCutsHistory, setCashCutsHistory] = useState([]);
    // Chargebacks state (pending only — applied ones are consumed on next commission)
    const [chargebacks, setChargebacks] = useState([]);

    const loadChargebacks = async (sellerId = null) => {
        try {
            const params = new URLSearchParams({ status: 'pending' });
            if (sellerId) params.set('seller_id', sellerId);
            const { data } = await axios.get(`${API}/chargebacks?${params}`, { headers: authHeaders() });
            setChargebacks(data || []);
        } catch { /* silent */ }
    };

    const applyChargeback = async (cbId) => {
        try {
            await axios.post(`${API}/chargebacks/${cbId}/apply`, { note: '' }, { headers: authHeaders() });
            toast.success('Chargeback aplicado — se descontará en la próxima comisión de la vendedora.');
            await loadChargebacks(selectedSeller || null);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'No se pudo aplicar');
        }
    };

    const dismissChargeback = async (cbId) => {
        try {
            await axios.post(`${API}/chargebacks/${cbId}/dismiss`, { reason: 'Descartado' }, { headers: authHeaders() });
            toast.success('Chargeback descartado');
            await loadChargebacks(selectedSeller || null);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'No se pudo descartar');
        }
    };

    useEffect(() => {
        (async () => {
            try {
                const [u, h, c] = await Promise.all([
                    axios.get(`${API}/users`, { headers: authHeaders() }),
                    axios.get(`${API}/commissions`, { headers: authHeaders() }),
                    axios.get(`${API}/company`, { headers: authHeaders() })
                ]);
                setSellers((u.data || []).filter(x =>
                    (x.role === 'seller' || x.role === 'manager') && x.commission_eligible !== false
                ));
                setHistory(h.data || []);
                setCompany(c.data || {});
                await loadChargebacks();
            } catch (e) { /* non-fatal */ }
        })();
    }, []);

    const addBonus = () => setBonuses([...bonuses, { type: 'objective', description: '', amount: 0 }]);
    const addDeduction = () => setDeductions([...deductions, { concept: '', amount: 0 }]);
    const removeBonus = (i) => setBonuses(bonuses.filter((_, idx) => idx !== i));
    const removeDeduction = (i) => setDeductions(deductions.filter((_, idx) => idx !== i));

    const generate = async () => {
        if (!selectedSeller) return toast.error('Selecciona un vendedor');
        try {
            const res = await axios.post(`${API}/commissions/generate`, {
                seller_id: selectedSeller,
                date_from: dateFrom || null,
                date_to: dateTo || null,
                bonuses: bonuses.filter(b => b.amount > 0),
                deductions: deductions.filter(d => d.amount > 0),
                payment_method: paymentMethod,
                notes
            }, { headers: authHeaders() });
            setPreview(res.data);
            setShowReceipt(true);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al generar');
        }
    };

    const confirmPayment = async () => {
        setConfirming(true);
        try {
            const res = await axios.post(`${API}/commissions/confirm`, { commission_id: preview.id }, { headers: authHeaders() });
            const saved = res.data || preview;
            toast.success('Comisión pagada y registrada');
            // Update preview with saved doc (with id) so PDF download works
            setPreview(saved);
            // Refresh history
            const h = await axios.get(`${API}/commissions`, { headers: authHeaders() });
            setHistory(h.data);
        } catch (err) {
            const detail = err.response?.data?.detail || 'Error al confirmar';
            if (err.response?.status === 409) {
                // Cash-cut gate failed → open the cash-cut sheet automatically
                toast.error(detail);
                setCashCutSeller(selectedSeller);
                setShowCashCut(true);
                await loadPendingCut(selectedSeller);
            } else {
                toast.error(detail);
            }
        } finally {
            setConfirming(false);
        }
    };

    // ====== Cash cut handlers ======
    const loadPendingCut = async (sellerId, untilDate = null) => {
        if (!sellerId) return;
        setLoadingCut(true);
        try {
            const data = await getPendingCashCut(sellerId, untilDate || null);
            setPendingCut(data);
            setCashReceived(String(data?.totals?.cash || 0));
            // Also load history of this seller
            const hist = await listCashCuts({ sellerId });
            setCashCutsHistory(hist || []);
        } catch (e) {
            toast.error('No se pudo cargar el corte pendiente');
        } finally {
            setLoadingCut(false);
        }
    };

    const openCashCut = async () => {
        setShowCashCut(true);
        setCashCutUntilDate('');
        if (selectedSeller) {
            setCashCutSeller(selectedSeller);
            await loadPendingCut(selectedSeller);
        } else {
            setPendingCut(null);
        }
    };

    const handleSaveCashCut = async () => {
        if (!cashCutSeller) {
            toast.error('Selecciona una promotora');
            return;
        }
        if (!pendingCut || pendingCut.totals.sales_count === 0) {
            toast.error('No hay ventas pendientes para cortar');
            return;
        }
        setSavingCut(true);
        try {
            const cut = await createCashCut({
                seller_id: cashCutSeller,
                cash_received: parseFloat(cashReceived) || 0,
                notes: cashCutNotes,
                until_date: cashCutUntilDate || null,
            });
            toast.success(`Corte ${cut.folio} guardado. Ahora puedes confirmar la comisión.`);
            // Auto-download the receipt for signature
            try { await downloadCashCutPdf(cut.id, cut.folio); } catch { /* noop */ }
            setCashCutNotes('');
            setCashCutUntilDate('');
            await loadPendingCut(cashCutSeller);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al guardar el corte');
        } finally {
            setSavingCut(false);
        }
    };

    const fetchPdfBlob = async (kind) => {
        const isSaved = preview.status === 'paid' && preview.id;
        const res = isSaved
            ? await axios.get(`${API}/commissions/${preview.id}/pdf?doc=${kind}`, { headers: authHeaders(), responseType: 'blob' })
            : await axios.post(`${API}/commissions/preview-pdf?doc=${kind}`, preview, { headers: authHeaders(), responseType: 'blob' });
        return new Blob([res.data], { type: 'application/pdf' });
    };

    const downloadPreviewPdf = async (kind = 'receipt') => {
        if (!preview) return;
        try {
            const blob = await fetchPdfBlob(kind);
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `${kind === 'detail' ? 'detalle' : 'recibo'}-${(preview.seller_name || 'vendedor').replace(/\s/g, '_')}-${(preview.id || 'preview').slice(0, 8)}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(link.href);
        } catch (err) {
            toast.error('No se pudo generar el PDF');
        }
    };

    // Imprime SOLO el PDF (recibo o detalle) en un iframe oculto.
    const printPreviewPdf = async (kind = 'receipt') => {
        if (!preview) return;
        try {
            const blob = await fetchPdfBlob(kind);
            const blobUrl = URL.createObjectURL(blob);
            const iframe = document.createElement('iframe');
            iframe.style.position = 'fixed';
            iframe.style.right = '0';
            iframe.style.bottom = '0';
            iframe.style.width = '0';
            iframe.style.height = '0';
            iframe.style.border = '0';
            iframe.src = blobUrl;
            iframe.onload = () => {
                try {
                    iframe.contentWindow.focus();
                    iframe.contentWindow.print();
                } catch (e) {
                    // Fallback: open the blob in a new tab so the user can print from there
                    window.open(blobUrl, '_blank');
                }
                // Clean up after a delay so the dialog has time to open
                setTimeout(() => {
                    document.body.removeChild(iframe);
                    URL.revokeObjectURL(blobUrl);
                }, 60000);
            };
            document.body.appendChild(iframe);
        } catch (err) {
            toast.error('No se pudo preparar la impresión');
        }
    };

    const openHistoryReceipt = async (commissionId) => {
        try {
            const { data } = await axios.get(`${API}/commissions/${commissionId}`, { headers: authHeaders() });
            setPreview({ ...data, fromHistory: true });
            setShowReceipt(true);
        } catch {
            toast.error('No se pudo abrir el recibo');
        }
    };

    const downloadHistoryPdf = async (commissionId, sellerName) => {
        try {
            const res = await axios.get(`${API}/commissions/${commissionId}/pdf`, {
                headers: authHeaders(),
                responseType: 'blob'
            });
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `comision-${(sellerName || 'vendedor').replace(/\s/g, '_')}-${commissionId.slice(0, 8)}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(link.href);
        } catch (err) {
            toast.error('No se pudo descargar el PDF');
        }
    };

    return (
        <div className="space-y-6 animate-fade-up max-w-[1400px]" data-testid="commissions-page">
            <div className="flex items-end justify-between pb-6 border-b border-[#ECE3D2]">
                <div>
                    <div className="flex items-center gap-2 text-xs mb-2">
                        <span className="text-[#8B8474]">Operación</span>
                        <span className="text-[#C7BCA5]">/</span>
                        <span className="text-[#1F2D4B] font-medium">Comisiones</span>
                    </div>
                    <h1 className="font-display text-3xl font-bold text-[#1F2D4B]">Comisiones</h1>
                    <p className="text-sm text-[#4B5878] mt-1.5">Genera cortes de comisión por vendedor</p>
                </div>
                <Button
                    onClick={openCashCut}
                    data-testid="open-cash-cut-btn"
                    className="h-10 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-md font-medium"
                >
                    <Wallet size={16} weight="duotone" className="mr-1.5" />
                    Corte de caja
                </Button>
            </div>

            {/* Generator */}
            <div className="bg-white border border-[#ECE3D2] rounded-lg p-6">
                <h2 className="font-display font-bold text-[#1F2D4B] mb-4 flex items-center gap-2">
                    <Calculator size={18} weight="duotone" /> Generar nuevo corte
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                    <div>
                        <Label className="text-xs font-semibold text-[#1F2D4B] mb-1.5 block">Vendedor *</Label>
                        <Select value={selectedSeller} onValueChange={setSelectedSeller}>
                            <SelectTrigger className="h-10 rounded-md border-[#ECE3D2]" data-testid="seller-select">
                                <SelectValue placeholder="Selecciona..." />
                            </SelectTrigger>
                            <SelectContent>
                                {sellers.map(s => (
                                    <SelectItem key={s.id} value={s.id}>
                                        {s.name} <span className="text-[#8B8474] text-xs ml-1">· {s.commission_percent || 0}%</span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold text-[#1F2D4B] mb-1.5 block">Desde</Label>
                        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="date-from-input" className="h-10 rounded-md border-[#ECE3D2]" />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold text-[#1F2D4B] mb-1.5 block">Hasta</Label>
                        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="date-to-input" className="h-10 rounded-md border-[#ECE3D2]" />
                    </div>
                </div>

                {/* Bonuses */}
                <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                        <Label className="text-xs font-semibold text-[#1F2D4B]">Bonos (+)</Label>
                        <Button type="button" size="sm" variant="outline" onClick={addBonus} data-testid="add-bonus-btn" className="h-7 text-xs rounded-md border-[#ECE3D2] text-[#1F2D4B]">
                            <Plus size={12} className="mr-1" /> Agregar bono
                        </Button>
                    </div>
                    {bonuses.length === 0 ? (
                        <p className="text-xs text-[#8B8474] italic">Sin bonos</p>
                    ) : (
                        <div className="space-y-2">
                            {bonuses.map((b, i) => (
                                <div key={i} className="grid grid-cols-[140px_1fr_140px_120px_36px] gap-2">
                                    <Select value={b.type} onValueChange={(v) => setBonuses(bonuses.map((x, idx) => idx === i ? { ...x, type: v } : x))}>
                                        <SelectTrigger className="h-9 rounded-md border-[#ECE3D2] text-xs"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {BONUS_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                    <Input placeholder="Descripción" value={b.description} onChange={(e) => setBonuses(bonuses.map((x, idx) => idx === i ? { ...x, description: e.target.value } : x))} className="h-9 rounded-md border-[#ECE3D2] text-sm" />
                                    <Input type="date" title="Día del bono (opcional)" value={b.date || ''} onChange={(e) => setBonuses(bonuses.map((x, idx) => idx === i ? { ...x, date: e.target.value || null } : x))} data-testid={`bonus-date-${i}`} className="h-9 rounded-md border-[#ECE3D2] text-xs" />
                                    <Input type="number" min={0} placeholder="$0" value={b.amount} onChange={(e) => setBonuses(bonuses.map((x, idx) => idx === i ? { ...x, amount: parseFloat(e.target.value) || 0 } : x))} className="h-9 rounded-md border-[#ECE3D2] text-sm" />
                                    <Button type="button" variant="outline" onClick={() => removeBonus(i)} className="h-9 w-9 p-0 rounded-md border-[#ECE3D2] text-red-600"><Trash size={12} /></Button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Deductions */}
                <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                        <Label className="text-xs font-semibold text-[#1F2D4B]">Deducciones (−)</Label>
                        <Button type="button" size="sm" variant="outline" onClick={addDeduction} data-testid="add-deduction-btn" className="h-7 text-xs rounded-md border-[#ECE3D2] text-[#1F2D4B]">
                            <Plus size={12} className="mr-1" /> Agregar deducción
                        </Button>
                    </div>
                    {deductions.length === 0 ? (
                        <p className="text-xs text-[#8B8474] italic">Sin deducciones</p>
                    ) : (
                        <div className="space-y-2">
                            {deductions.map((d, i) => (
                                <div key={i} className="grid grid-cols-[1fr_120px_36px] gap-2">
                                    <Input placeholder="Concepto" value={d.concept} onChange={(e) => setDeductions(deductions.map((x, idx) => idx === i ? { ...x, concept: e.target.value } : x))} className="h-9 rounded-md border-[#ECE3D2] text-sm" />
                                    <Input type="number" min={0} placeholder="$0" value={d.amount} onChange={(e) => setDeductions(deductions.map((x, idx) => idx === i ? { ...x, amount: parseFloat(e.target.value) || 0 } : x))} className="h-9 rounded-md border-[#ECE3D2] text-sm" />
                                    <Button type="button" variant="outline" onClick={() => removeDeduction(i)} className="h-9 w-9 p-0 rounded-md border-[#ECE3D2] text-red-600"><Trash size={12} /></Button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                    <div>
                        <Label className="text-xs font-semibold text-[#1F2D4B] mb-1.5 block">Forma de pago</Label>
                        <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                            <SelectTrigger className="h-10 rounded-md border-[#ECE3D2]"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="cash">Efectivo</SelectItem>
                                <SelectItem value="transfer">Transferencia</SelectItem>
                                <SelectItem value="check">Cheque</SelectItem>
                                <SelectItem value="card">Tarjeta</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold text-[#1F2D4B] mb-1.5 block">Notas (opcional)</Label>
                        <Input value={notes} onChange={(e) => setNotes(e.target.value)} className="h-10 rounded-md border-[#ECE3D2]" />
                    </div>
                </div>

                <Button onClick={generate} data-testid="generate-commission-btn" className="h-10 px-4 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-md font-medium btn-lift">
                    <Lightning size={14} weight="bold" className="mr-1.5" /> Generar preliminar
                </Button>
            </div>

            {/* Chargebacks pending (cancellations after cash cut) */}
            {chargebacks.length > 0 && (
                <div className="bg-white border border-rose-200 rounded-lg p-6" data-testid="chargebacks-panel">
                    <h2 className="font-display font-bold text-rose-700 mb-1 flex items-center gap-2">
                        Chargebacks pendientes
                    </h2>
                    <p className="text-xs text-[#4B5878] mb-4">
                        Ventas canceladas después de un corte cerrado. Al aplicar, se descontará automáticamente en la próxima comisión de la promotora, referenciada por folio.
                    </p>
                    <div className="divide-y divide-[#ECE3D2]">
                        {chargebacks.map(cb => (
                            <div key={cb.id} className="flex items-center justify-between py-3 gap-3 flex-wrap" data-testid={`chargeback-row-${cb.id}`}>
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm font-semibold text-[#1F2D4B]">
                                        {cb.seller_name || cb.seller_id?.slice(0, 8)} — {(cb.refunded_folios || []).join(', ')}
                                    </p>
                                    <p className="text-[11px] text-[#8B8474]">
                                        Corte previo: {cb.cash_cut_folio || '—'} · Devolución: {cb.refund_folio} · Motivo: {cb.reason || '—'}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="font-mono font-bold text-rose-700 text-sm">
                                        ${(cb.amount || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                    </span>
                                    <Button size="sm" onClick={() => applyChargeback(cb.id)} data-testid={`apply-chargeback-${cb.id}`} className="h-8 px-3 bg-rose-600 hover:bg-rose-700 text-white text-xs">
                                        Aplicar
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => dismissChargeback(cb.id)} data-testid={`dismiss-chargeback-${cb.id}`} className="h-8 px-3 text-xs">
                                        Descartar
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* History */}
            <div className="bg-white border border-[#ECE3D2] rounded-lg p-6">
                <h2 className="font-display font-bold text-[#1F2D4B] mb-4 flex items-center gap-2">
                    <FileText size={18} weight="duotone" /> Historial de pagos
                </h2>
                {history.length === 0 ? (
                    <p className="text-sm text-[#8B8474] italic">Sin comisiones pagadas</p>
                ) : (
                    <div className="space-y-2">
                        {history.map(h => (
                            <div key={h.id} className="flex items-center justify-between py-2.5 border-b border-[#ECE3D2] last:border-0" data-testid={`history-row-${h.id}`}>
                                <div>
                                    <p className="text-sm font-semibold text-[#1F2D4B]">{h.seller_name}</p>
                                    <p className="text-[11px] text-[#8B8474]">
                                        {h.date_from || 'Inicio'} → {h.date_to || 'hoy'} · {h.sales_count} ventas · Pagado {fmtDateTimeMx(h.paid_at || h.created_at)}
                                    </p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="text-right">
                                        <p className="font-display text-lg font-bold" style={{ color: '#65C76A' }}>{fmt(h.net_amount)}</p>
                                        <span className="status-badge status-paid">Pagado</span>
                                    </div>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => openHistoryReceipt(h.id)}
                                        data-testid={`view-receipt-${h.id}`}
                                        className="h-9 px-3 rounded-md border-[#ECE3D2] text-[#1F2D4B] hover:bg-[#F8F4EC]"
                                    >
                                        <FileText size={14} className="mr-1" /> Ver
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => downloadHistoryPdf(h.id, h.seller_name)}
                                        data-testid={`download-pdf-${h.id}`}
                                        className="h-9 px-3 rounded-md border-[#ECE3D2] text-[#1F2D4B] hover:bg-[#F8F4EC]"
                                        title="Descargar PDF"
                                    >
                                        <DownloadSimple size={14} weight="bold" />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Receipt Modal */}
            <Dialog open={showReceipt} onOpenChange={setShowReceipt}>
                <DialogContent className="!block sm:max-w-3xl !max-h-[95vh] !w-[95vw] !p-0 !gap-0 bg-white border border-[#ECE3D2] rounded-xl overflow-hidden">
                    <DialogHeader className="p-6 pb-2 border-b border-[#ECE3D2]">
                        <DialogTitle className="font-display text-xl font-bold text-[#1F2D4B]">{preview?.status === 'paid' ? 'Recibo de comisión pagado' : 'Preliminar de comisión'}</DialogTitle>
                        <DialogDescription className="text-xs text-[#4B5878]">{preview?.status === 'paid' ? 'Pago registrado; las ventas quedaron marcadas como pagadas' : 'Revisa antes de confirmar el pago'}</DialogDescription>
                    </DialogHeader>

                    {preview && (
                        <div id="receipt-print" className="p-5 sm:p-8 overflow-y-auto max-h-[calc(95vh-320px)] sm:max-h-[calc(95vh-200px)]">
                            {/* Company header */}
                            <div className="flex items-start justify-between mb-6 pb-5 border-b-2 border-[#1F2D4B]">
                                <div>
                                    <img src="/brand/exploremos-logo-navy.png" alt="Exploremos" className="h-16 mb-2" />
                                    <p className="font-semibold text-[#1F2D4B]">{company.legal_name || 'Exploremos'}</p>
                                    {company.rfc && <p className="text-xs text-[#4B5878]">RFC: {company.rfc}</p>}
                                    {company.address && <p className="text-xs text-[#4B5878]">{company.address}</p>}
                                </div>
                                <div className="text-right">
                                    <p className="text-[10px] uppercase tracking-wider text-[#8B8474]">Recibo de comisión</p>
                                    <p className="font-mono text-xs text-[#1F2D4B]" data-testid="receipt-created-at">Generado: {fmtDateTimeMx(preview.created_at)}</p>
                                    {preview.paid_at && (
                                        <p className="font-mono text-xs font-semibold text-[#065F46]" data-testid="receipt-paid-at">Pagado: {fmtDateTimeMx(preview.paid_at)}</p>
                                    )}
                                </div>
                            </div>

                            <div className="mb-5">
                                <p className="text-[10px] uppercase tracking-wider text-[#8B8474] mb-1">Recibe</p>
                                <p className="font-display text-xl font-bold text-[#1F2D4B]">{preview.seller_name}</p>
                                <p className="text-xs text-[#4B5878]">
                                    Periodo: {preview.date_from || 'Inicio'} al {preview.date_to || 'hoy'} · {preview.sales_count} ventas
                                </p>
                            </div>

                            {preview.status !== 'paid' && preview.older_unpaid && (
                                <div className="mb-5 p-3 rounded-md border border-amber-300 bg-amber-50 text-xs text-amber-900" data-testid="older-unpaid-warning">
                                    <b>Atención:</b> {preview.older_unpaid.count} venta(s) del {fmtDate(preview.older_unpaid.first_date)} al {fmtDate(preview.older_unpaid.last_date)} nunca se han pagado.
                                    {' '}El último recibo pagado (#{preview.older_unpaid.last_receipt_id.slice(0, 8).toUpperCase()}) cubrió
                                    {' '}{preview.older_unpaid.last_receipt_from ? fmtDate(preview.older_unpaid.last_receipt_from) : 'el inicio'} → {preview.older_unpaid.last_receipt_to ? fmtDate(preview.older_unpaid.last_receipt_to) : 'su fecha de pago'}
                                    {' '}y no las incluía. No son ventas de ese recibo; revisa los folios abajo antes de pagar.
                                </div>
                            )}

                            <ReceiptDayBoxes commission={preview} />

                            <SalesDetailTable commission={preview} />

                            <div className="space-y-1.5 mb-5">
                                <ReceiptTotals commission={preview} />
                                <p className="text-[10px] text-[#4B5878] mt-1 italic font-medium uppercase tracking-wide">
                                    ({preview.net_amount < 0 ? 'MENOS ' : ''}{numberToSpanishMXN(preview.net_amount)})
                                </p>
                                <p className="text-xs text-[#8B8474] mt-2">Forma de pago: <span className="font-semibold capitalize">{PAYMENT_LABEL_ES[preview.payment_method] || preview.payment_method}</span></p>
                                {company.commission_receipt_legend && (
                                    <p className="text-[11px] text-[#4B5878] mt-4 pt-3 border-t border-[#ECE3D2] italic whitespace-pre-line" data-testid="commission-receipt-legend">
                                        {company.commission_receipt_legend
                                            .replace(/\{seller_name\}/g, preview.seller_name || '')
                                            .replace(/\{net_amount\}/g, fmt(preview.net_amount))
                                            .replace(/\{net_amount_words\}/g, numberToSpanishMXN(preview.net_amount))
                                            .replace(/\{date\}/g, fmtDateMx(preview.paid_at || preview.created_at))}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="flex flex-wrap gap-2 p-4 sm:p-6 pt-4 border-t border-[#ECE3D2] bg-white">
                        <Button variant="outline" onClick={() => {
                            setShowReceipt(false);
                            if (preview?.status === 'paid' && !preview?.fromHistory) {
                                // reset form after successful pay
                                setPreview(null);
                                setBonuses([]); setDeductions([]); setNotes(''); setSelectedSeller('');
                            }
                        }} className="h-10 rounded-md border-[#ECE3D2]">
                            Cerrar
                        </Button>
                        <Button onClick={() => printPreviewPdf('receipt')} variant="outline" data-testid="print-receipt-btn" className="h-10 rounded-md border-[#ECE3D2] text-[#1F2D4B]">
                            <Printer size={14} className="mr-1.5" /> Imprimir recibo
                        </Button>
                        <Button onClick={() => printPreviewPdf('detail')} variant="outline" data-testid="print-detail-btn" className="h-10 rounded-md border-[#ECE3D2] text-[#1F2D4B]">
                            <ListMagnifyingGlass size={14} className="mr-1.5" /> Imprimir detalle
                        </Button>
                        <Button onClick={() => downloadPreviewPdf('receipt')} variant="outline" data-testid="download-preview-pdf-btn" title="Descargar recibo en PDF" className="h-10 w-10 p-0 rounded-md border-[#ECE3D2] text-[#1F2D4B]">
                            <DownloadSimple size={14} weight="bold" />
                        </Button>
                        {preview?.status !== 'paid' && (
                            <Button onClick={confirmPayment} disabled={confirming || !preview} data-testid="confirm-commission-btn" className="flex-1 min-w-full sm:min-w-0 h-10 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-md font-medium btn-lift">
                                <CheckCircle size={14} weight="bold" className="mr-1.5" />
                                {confirming ? 'Confirmando...' : 'Confirmar y marcar pagado'}
                            </Button>
                        )}
                        {preview?.status === 'paid' && (
                            <div className="flex-1 h-10 flex items-center justify-center bg-[#D1FAE5] text-[#065F46] rounded-md text-sm font-semibold gap-1.5" data-testid="commission-paid-badge">
                                <CheckCircle size={16} weight="bold" /> Comisión pagada
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* ===== CASH CUT SHEET ===== */}
            <Sheet open={showCashCut} onOpenChange={setShowCashCut}>
                <SheetContent
                    side="right"
                    className="w-full sm:max-w-xl overflow-y-auto bg-white"
                    data-testid="cash-cut-sheet"
                >
                    <SheetHeader>
                        <SheetTitle className="text-[#1F2D4B] flex items-center gap-2">
                            <Wallet size={22} weight="duotone" className="text-[#FB7185]" />
                            Corte de caja
                        </SheetTitle>
                        <SheetDescription className="text-[#8B8474]">
                            Indispensable antes de pagar comisiones. Genera el arqueo desde el último corte y emite el recibo para que la promotora firme la entrega de efectivo.
                        </SheetDescription>
                    </SheetHeader>

                    <div className="mt-5 space-y-4">
                        {/* Seller selector */}
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-[#1F2D4B] font-semibold mb-1.5 block">
                                Promotora
                            </Label>
                            <Select
                                value={cashCutSeller}
                                onValueChange={async (v) => {
                                    setCashCutSeller(v);
                                    await loadPendingCut(v, cashCutUntilDate || null);
                                }}
                            >
                                <SelectTrigger className="h-10 rounded-md border-[#ECE3D2] bg-white" data-testid="cash-cut-seller-select">
                                    <SelectValue placeholder="Selecciona…" />
                                </SelectTrigger>
                                <SelectContent>
                                    {sellers.map(s => (
                                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Partial cut date picker */}
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-[#1F2D4B] font-semibold mb-1.5 block">
                                Cortar hasta (opcional · corte parcial)
                            </Label>
                            <Input
                                type="date"
                                value={cashCutUntilDate}
                                onChange={async (e) => {
                                    setCashCutUntilDate(e.target.value);
                                    if (cashCutSeller) {
                                        await loadPendingCut(cashCutSeller, e.target.value || null);
                                    }
                                }}
                                data-testid="cash-cut-until-date"
                                className="h-10 rounded-md border-[#ECE3D2] bg-white"
                            />
                            <p className="text-[11px] text-[#8B8474] mt-1">
                                Sólo se incluirán las ventas creadas hasta esta fecha (23:59 hora MX). Deja vacío para cortar todo lo pendiente.
                            </p>
                        </div>

                        {loadingCut && (
                            <div className="text-center py-8 text-[#8B8474]">
                                <SpinnerGap size={22} className="animate-spin inline mr-2" />
                                Cargando ventas pendientes…
                            </div>
                        )}

                        {!loadingCut && pendingCut && (
                            <>
                                {pendingCut.totals.sales_count === 0 ? (
                                    <div className="p-4 rounded-md bg-[#ECFDF5] border border-[#A7F3D0] text-[#065F46] text-sm flex gap-2">
                                        <CheckCircle size={18} weight="duotone" />
                                        <div>
                                            <p className="font-semibold">Sin ventas pendientes</p>
                                            <p className="text-xs mt-0.5">No hay ventas que cortar para esta promotora. Ya está al día.</p>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        {/* Totals card */}
                                        <div className="bg-white border border-[#ECE3D2] rounded-lg p-4 space-y-2.5" data-testid="cash-cut-totals">
                                            <div className="flex justify-between text-sm">
                                                <span className="text-[#8B8474]">Ventas a cortar</span>
                                                <span className="font-bold text-[#1F2D4B]">{pendingCut.totals.sales_count}</span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <span className="text-[#8B8474]">Total facturado</span>
                                                <span className="font-bold text-[#1F2D4B]">{fmt(pendingCut.totals.total_amount)}</span>
                                            </div>
                                            <div className="border-t border-[#F4ECE0] pt-2 space-y-1.5 text-sm">
                                                <div className="flex justify-between">
                                                    <span className="text-[#8B8474]">· Efectivo</span>
                                                    <span className="font-mono text-[#1F2D4B]">{fmt(pendingCut.totals.cash)}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-[#8B8474]">· T. Crédito</span>
                                                    <span className="font-mono text-[#1F2D4B]">{fmt(pendingCut.totals.credit_card)}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-[#8B8474]">· T. Débito</span>
                                                    <span className="font-mono text-[#1F2D4B]">{fmt(pendingCut.totals.debit_card)}</span>
                                                </div>
                                                {pendingCut.totals.courtesy_count > 0 && (
                                                    <div className="flex justify-between text-xs text-[#8B8474]">
                                                        <span>Cortesías</span>
                                                        <span>{pendingCut.totals.courtesy_count}</span>
                                                    </div>
                                                )}
                                            </div>
                                            {pendingCut.since && (
                                                <p className="text-[10px] uppercase tracking-wider text-[#8B8474] pt-1">
                                                    Desde el último corte: {new Date(pendingCut.since).toLocaleString('es-MX', { timeZone: 'America/Mexico_City', dateStyle: 'medium', timeStyle: 'short' })}
                                                </p>
                                            )}
                                        </div>

                                        {/* Cash arqueo */}
                                        <div className="bg-[#FFF8E7] border border-[#F5DEB3] rounded-lg p-4 space-y-3">
                                            <p className="text-xs uppercase tracking-wider text-[#92400E] font-semibold flex items-center gap-1.5">
                                                <Receipt size={13} weight="bold" /> Arqueo de efectivo
                                            </p>
                                            <div>
                                                <Label className="text-[11px] uppercase tracking-wider text-[#1F2D4B] font-semibold mb-1 block">
                                                    Efectivo recibido de la promotora
                                                </Label>
                                                <Input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    value={cashReceived}
                                                    onChange={(e) => setCashReceived(e.target.value)}
                                                    placeholder="0.00"
                                                    data-testid="cash-received-input"
                                                    style={{ backgroundColor: '#FFFFFF' }}
                                                    className="h-10 border-[#ECE3D2] focus:border-[#1F2D4B] font-mono text-lg"
                                                />
                                                <p className="text-[11px] text-[#92400E] mt-1.5">
                                                    Esperado: <b className="font-mono">{fmt(pendingCut.totals.cash)}</b>
                                                    {' · '}
                                                    Diferencia: <b className={`font-mono ${Math.abs((parseFloat(cashReceived)||0) - pendingCut.totals.cash) < 0.01 ? 'text-[#065F46]' : 'text-[#B91C1C]'}`}>
                                                        {fmt((parseFloat(cashReceived)||0) - pendingCut.totals.cash)}
                                                    </b>
                                                </p>
                                            </div>
                                            <div>
                                                <Label className="text-[11px] uppercase tracking-wider text-[#1F2D4B] font-semibold mb-1 block">
                                                    Notas
                                                </Label>
                                                <textarea
                                                    value={cashCutNotes}
                                                    onChange={(e) => setCashCutNotes(e.target.value)}
                                                    placeholder="Ej. La promotora entregó faltante por X razón…"
                                                    data-testid="cash-cut-notes"
                                                    style={{ backgroundColor: '#FFFFFF' }}
                                                    className="w-full min-h-[60px] p-2.5 rounded-md border border-[#ECE3D2] text-sm focus:outline-none focus:border-[#1F2D4B]"
                                                />
                                            </div>
                                            <Button
                                                onClick={handleSaveCashCut}
                                                disabled={savingCut}
                                                data-testid="save-cash-cut-btn"
                                                className="w-full h-11 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-md font-medium"
                                            >
                                                {savingCut ? (
                                                    <SpinnerGap size={14} className="animate-spin mr-1.5" />
                                                ) : (
                                                    <Wallet size={14} weight="bold" className="mr-1.5" />
                                                )}
                                                Cerrar corte y emitir recibo
                                            </Button>
                                        </div>
                                    </>
                                )}
                            </>
                        )}

                        {/* History of cash cuts */}
                        {cashCutsHistory.length > 0 && (
                            <div className="pt-2">
                                <h4 className="text-xs uppercase tracking-wider text-[#1F2D4B] font-semibold mb-2">
                                    Últimos cortes de esta promotora
                                </h4>
                                <div className="space-y-2">
                                    {cashCutsHistory.slice(0, 5).map((c) => (
                                        <div
                                            key={c.id}
                                            className="flex items-center justify-between p-3 bg-white border border-[#ECE3D2] rounded-md"
                                            data-testid={`cash-cut-history-${c.id}`}
                                        >
                                            <div className="min-w-0 flex-1">
                                                <p className="font-bold text-[#1F2D4B] text-sm">{c.folio}</p>
                                                <p className="text-[11px] text-[#8B8474]">
                                                    {new Date(c.created_at).toLocaleString('es-MX', { timeZone: 'America/Mexico_City', dateStyle: 'medium', timeStyle: 'short' })}
                                                    {' · '}{c.totals?.sales_count || 0} ventas · {fmt(c.totals?.total_amount || 0)}
                                                </p>
                                                {Math.abs(c.cash_difference || 0) > 0.01 && (
                                                    <p className="text-[11px] text-[#B91C1C] flex items-center gap-1 mt-0.5">
                                                        <Warning size={10} weight="bold" />
                                                        Diferencia: {fmt(c.cash_difference)}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-1.5">
                                                {canAuditCuts && (
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => setAuditCutId(c.id)}
                                                        data-testid={`cash-cut-audit-btn-${c.id}`}
                                                        className="h-8 text-xs border-[#1F2D4B] bg-[#1F2D4B] text-white hover:bg-[#0F1B3D]"
                                                        title="Ver detalle por folios y forma de pago"
                                                    >
                                                        <ListMagnifyingGlass size={12} weight="bold" className="mr-1" />
                                                        Detalle
                                                    </Button>
                                                )}
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => downloadCashCutPdf(c.id, c.folio)}
                                                    className="h-8 text-xs border-[#ECE3D2] text-[#1F2D4B] hover:bg-[#F4ECE0]"
                                                >
                                                    <DownloadSimple size={12} weight="bold" className="mr-1" />
                                                    PDF
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </SheetContent>
            </Sheet>

            {/* Audit modal for cash cuts (admin-only) */}
            <CashCutAuditModal
                cutId={auditCutId}
                open={!!auditCutId}
                onClose={() => setAuditCutId(null)}
            />
        </div>
    );
};

export default CommissionsPage;
